import { hexToCss, hexToCIELab, hexToCmyk, hexToRgb, hexToXyz, hexToHsl, hexToHsv, rgbToCss, rgbToCIELab, rgbToCmyk, rgbToHex, rgbToHsl, rgbToHsv, rgbToXyz, hslToCss, hslToHex, hslToRgb, hslToHsv, hslToCmyk, hslToXyz, hslToCIELab, hsvToCss, hsvToHex, hsvToRgb, hsvToHsl, hsvToCmyk, hsvToXyz, hsvToCIELab, cmykToCss, cmykToHex, cmykToRgb, cmykToHsl, cmykToHsv, cmykToXyz, cmykToCIELab, xyzToCss, xyzToHex, xyzToRgb, xyzToHsl, xyzToHsv, xyzToCmyk, xyzToCIELab, labToCss, labToXyz, labToCmyk, labToHsv, labToRgb, labToHex, labToHsl } from "../converters";
import { cie76, cie94, cie2000 } from "../calculators";
import { cssValues } from "../utils";
import { InvalidColorError, isValidCmyk, isValidCssRgb, isValidHex, isValidHsl, isValidHsv, isValidLab, isValidRgb, isValidXyz } from "../validators";
import { A, B, black, blue, cyan, green, hueHsl, hueHsv, L, lightness, magenta, red, saturationHsl, saturationHsv, value, X, Y, yellow, Z } from "../transformers";
import { complementary, tetradic, triadic } from "../theory";
const addTransformation = (color) => {
    color.red = (value) => red(color, value);
    color.green = (value) => green(color, value);
    color.blue = (value) => blue(color, value);
    color.hueHsl = (value) => hueHsl(color, value);
    color.saturationHsl = (value) => saturationHsl(color, value);
    color.lightness = (value) => lightness(color, value);
    color.hueHsv = (value) => hueHsv(color, value);
    color.saturationHsv = (value) => saturationHsv(color, value);
    color.value = (v) => value(color, v);
    color.cyan = (value) => cyan(color, value);
    color.magenta = (value) => magenta(color, value);
    color.yellow = (value) => yellow(color, value);
    color.black = (value) => black(color, value);
    color.X = (value) => X(color, value);
    color.Y = (value) => Y(color, value);
    color.Z = (value) => Z(color, value);
    color.L = (value) => L(color, value);
    color.A = (value) => A(color, value);
    color.B = (value) => B(color, value);
    return color;
};
const addDistanceCalculation = (color) => {
    color.distanceFrom = (clr) => ({
        cie76: cie76(color.lab(), clr.lab()),
        cie94: cie94(color.lab(), clr.lab()),
        cie2000: cie2000(color.lab(), clr.lab())
    });
    return color;
};
const addTheory = (color) => {
    color.complementary = () => fromHsl(complementary(color.hsl()));
    color.triadic = () => {
        const tr = triadic(color.hsl());
        return [
            fromHsl(tr[0]),
            fromHsl(tr[1])
        ];
    };
    color.tetradic = () => {
        const te = tetradic(color.hsl());
        return [
            fromHsl(te[0]),
            fromHsl(te[1]),
            fromHsl(te[2])
        ];
    };
    return color;
};
export const fromRgb = (rgb, options) => {
    if (!isValidRgb(rgb))
        throw new InvalidColorError(rgb, "RGB");
    const color = {
        rgb: () => rgb,
        hex: () => rgbToHex(rgb),
        css: () => rgbToCss(rgb, options),
        hsl: () => rgbToHsl(rgb, options),
        hsv: () => rgbToHsv(rgb, options),
        cmyk: () => rgbToCmyk(rgb),
        xyz: () => rgbToXyz(rgb, options),
        lab: () => rgbToCIELab(rgb, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromHsl = (hsl, options) => {
    if (!isValidHsl(hsl))
        throw new InvalidColorError(hsl, "HSL");
    const color = {
        hsl: () => hsl,
        hex: () => hslToHex(hsl),
        css: () => hslToCss(hsl, options),
        rgb: () => hslToRgb(hsl),
        hsv: () => hslToHsv(hsl, options),
        cmyk: () => hslToCmyk(hsl),
        xyz: () => hslToXyz(hsl, options),
        lab: () => hslToCIELab(hsl, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromHsv = (hsv, options) => {
    if (!isValidHsv(hsv))
        throw new InvalidColorError(hsv, "HSV");
    const color = {
        hsv: () => hsv,
        hex: () => hsvToHex(hsv),
        css: () => hsvToCss(hsv, options),
        rgb: () => hsvToRgb(hsv),
        hsl: () => hsvToHsl(hsv, options),
        cmyk: () => hsvToCmyk(hsv),
        xyz: () => hsvToXyz(hsv, options),
        lab: () => hsvToCIELab(hsv, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromCmyk = (cmyk, options) => {
    if (!isValidCmyk(cmyk))
        throw new InvalidColorError(cmyk, "CMYK");
    const color = {
        cmyk: () => cmyk,
        hex: () => cmykToHex(cmyk),
        css: () => cmykToCss(cmyk, options),
        rgb: () => cmykToRgb(cmyk),
        hsl: () => cmykToHsl(cmyk, options),
        hsv: () => cmykToHsv(cmyk, options),
        xyz: () => cmykToXyz(cmyk, options),
        lab: () => cmykToCIELab(cmyk, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromXyz = (xyz, options) => {
    if (!isValidXyz(xyz))
        throw new InvalidColorError(xyz, "XYZ");
    const color = {
        xyz: () => xyz,
        hex: () => xyzToHex(xyz),
        css: () => xyzToCss(xyz, options),
        rgb: () => xyzToRgb(xyz),
        hsl: () => xyzToHsl(xyz, options),
        hsv: () => xyzToHsv(xyz, options),
        cmyk: () => xyzToCmyk(xyz),
        lab: () => xyzToCIELab(xyz, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromLab = (lab, options) => {
    if (!isValidLab(lab))
        throw new InvalidColorError(lab, "CIELab");
    const color = {
        lab: () => lab,
        hex: () => labToHex(lab),
        css: () => labToCss(lab, options),
        rgb: () => labToRgb(lab),
        hsl: () => labToHsl(lab, options),
        hsv: () => labToHsv(lab, options),
        cmyk: () => labToCmyk(lab),
        xyz: () => labToXyz(lab, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
export const fromString = (value, options) => {
    try {
        if (value.toLowerCase().startsWith('rgb(')) {
            const [r, g, b] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromRgb({ r: parseInt(r), g: parseInt(g), b: parseInt(b) }, options);
        }
        else if (value.toLowerCase().startsWith('hsl(')) {
            const [h, s, l] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromHsl({ h: parseFloat(h), s: parseFloat(s), l: parseFloat(l) }, options);
        }
        else if (value.toLowerCase().startsWith('hsv(')) {
            const [h, s, v] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromHsv({ h: parseFloat(h), s: parseFloat(s), v: parseFloat(v) }, options);
        }
        else if (value.toLowerCase().startsWith('cmyk(')) {
            const [c, m, y, k] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromCmyk({ c: parseFloat(c), m: parseFloat(m), y: parseFloat(y), k: parseFloat(k) }, options);
        }
        else if (value.toLowerCase().startsWith('xyz(')) {
            const [x, y, z] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromXyz({ x: parseFloat(x), y: parseFloat(y), z: parseFloat(z) }, options);
        }
        else if (value.toLowerCase().startsWith('lab(')) {
            const [l, a, b] = value.substring(value.indexOf("(") + 1, value.indexOf(")")).split(",");
            return fromLab({ l: parseFloat(l), a: parseFloat(a), b: parseFloat(b) }, options);
        }
        else if (isValidHex(value)) {
            const color = {
                hex: () => value.startsWith('#') ? value.substring(1) : value.toLowerCase(),
                css: () => hexToCss(value, options),
                rgb: () => hexToRgb(value),
                hsl: () => hexToHsl(value, options),
                hsv: () => hexToHsv(value, options),
                cmyk: () => hexToCmyk(value),
                xyz: () => hexToXyz(value, options),
                lab: () => hexToCIELab(value, options)
            };
            return addTransformation(addDistanceCalculation(addTheory(color)));
        }
        else if (cssValues[value]) {
            const color = {
                hex: () => cssValues[value],
                css: () => hexToCss(cssValues[value], options),
                rgb: () => hexToRgb(cssValues[value]),
                hsl: () => hexToHsl(cssValues[value], options),
                hsv: () => hexToHsv(cssValues[value], options),
                cmyk: () => hexToCmyk(cssValues[value]),
                xyz: () => hexToXyz(cssValues[value], options),
                lab: () => hexToCIELab(cssValues[value], options)
            };
            return addTransformation(addDistanceCalculation(addTheory(color)));
        }
        throw new InvalidColorError(value, "String");
    }
    catch (error) {
        throw new InvalidColorError(value, "String");
    }
};
/**
 * @deprecated
 * Use the {@link fromString} function instead.
 */
export const fromHex = (hex, options) => {
    if (!isValidHex(hex))
        throw new InvalidColorError(hex, "HEX");
    const color = {
        hex: () => hex,
        css: () => hexToCss(hex, options),
        rgb: () => hexToRgb(hex),
        hsl: () => hexToHsl(hex, options),
        hsv: () => hexToHsv(hex, options),
        cmyk: () => hexToCmyk(hex),
        xyz: () => hexToXyz(hex, options),
        lab: () => hexToCIELab(hex, options)
    };
    return addTransformation(addDistanceCalculation(addTheory(color)));
};
/**
 * @deprecated
 * Use the {@link fromString} function instead.
 */
export const fromCss = (css, options) => {
    if (cssValues[css]) {
        return fromHex(cssValues[css], options);
    }
    else if (isValidCssRgb(css)) {
        const [r, g, b] = css.substring(4).replace(" ", "").split(",");
        return fromRgb({ r: parseInt(r), g: parseInt(g), b: parseInt(b) }, options);
    }
    else {
        throw new InvalidColorError(css, "CSS");
    }
};
